// CompanyProductEditDrawer.tsx

import {
  Box,
  Button,
  Drawer,
  IconButton,
  Typography,
  TextField,
  Divider,
  Alert,
} from '@mui/material';
import { TextStyle } from 'data/FontStyle';
import useWindowsDimension from 'functions/useWindowsDimension';
import {
  ICompanyProductAddViewModel,
  ICompanyProductViewModel,
} from 'Interfaces/ICompanyProductConfig';
import React, { useEffect, useState } from 'react';
import { usePost } from 'http/useApi';
import { rqPost } from 'http/apiRoutes';
import {
  EnumCompanyProductStatus,
  EnumOrderPostStatus,
  EnumPlacement,
} from 'Interfaces/IGlobalTypeConfig';

type CompanyProductEditDrawerProps = {
  buttonIcon?: React.ReactNode | null;
  buttonContext?: string;
  product?: any;
  companyId?: string;
  updateStatus: (status: EnumOrderPostStatus, id?: string | null) => void;
  isEnabled?: boolean; // Add isEnabled prop
  open?: boolean;
};
export const ConvertToCompanyProduct = (
  product: any,
  companyId: string
): ICompanyProductAddViewModel => {
  console.log('1. ConvertToCompanyProduct:', product);
  const status =
    product.status === EnumOrderPostStatus.PartialReceived
      ? EnumOrderPostStatus.PartialDelivered
      : EnumOrderPostStatus.Delivered;
  return {
    serviceTag: product.serviceTag,
    productId: product.productId,
    companyId: companyId,
    purchaserId: product.purchaserId,
    vendorId: product.vendorId || null,
    vendorProductUrl: product.vendorProductUrl || null,
    ownerId: product.ownerId || null,
    resourceId: product.resourceId || null,
    comment: product.comment ?? '',
    unitPrice: product.unitPrice || 0,
    retailPrice: product.retailPrice || 0,
    quantity: product.quantity || 1,
    discount: product.discount || 0,
    lifetime: product.product?.bindingPeriod !== 0 ? 3 : 99,
    securityValue: 0,
    status: EnumCompanyProductStatus.DELIVERED,
    productWarning: '',
    placement: product.ownerId ? EnumPlacement.EMPLOYEE : EnumPlacement.NONE,
    serialNumber: product.serialNumber,
    ticketProductId: product.id,
    orderPostStatus: status,
  } as ICompanyProductAddViewModel;
};

export const CompanyProductEditDrawer = ({
  buttonIcon,
  buttonContext,
  product,
  companyId,
  updateStatus,
  isEnabled = false, // Default to true
  open: initialOpen = false,
}: CompanyProductEditDrawerProps) => {
  const { width } = useWindowsDimension();
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<ICompanyProductAddViewModel>(
    product || {
      serviceTag: '',
      productId: '',
      companyId: '',
      purchaserId: '',
      ownerId: null,
      resourceId: null,
      comment: '',
      unitPrice: 0,
      retailPrice: 0,
      quantity: 1,
      discount: 0,
      lifetime: 0,
      securityValue: 0,
      status: 0,
      productWarning: '',
      placement: 0,
      serialNumber: null,
    }
  );
  const [isEnabledState, setIsEnabledState] = useState(isEnabled);

  console.log('1. initial product data:', product);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Initialize the POST mutation
  const postCompanyProduct = usePost(
    rqPost.companyProduct(isEnabledState),
    
  );

  // Handle form field changes
  const handleFieldChange = (
    field: keyof ICompanyProductViewModel,
    value: any
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  // Validate form before submission
  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // SerialNumber is required (based on your KRAV comment)
    if (!formData.serviceTag?.trim()) {
      newErrors.serviceTag = 'serviceTag is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission using the POST mutation
  const handleSubmit = (event: React.MouseEvent<HTMLElement>) => {
    if (!validateForm()) {
      return;
    }
    console.log('1. Edit payload:', formData);
    // Use the mutation to post the data
    postCompanyProduct.mutate(formData, {
      onSuccess: (data: ICompanyProductAddViewModel) => {
        updateStatus(
          formData.orderPostStatus || EnumOrderPostStatus.Delivered,
          formData.ticketProductId
        );
        console.log(
          'Edit product - Submitting company product update:',
          formData
        );

        // Optional: Handle success (e.g., show notification, update cache, etc.)
        console.log(
          'Edit product - Company product updated successfully:',
          formData
        );

        // Call the original handleChange with the original formData
        // handleChange(event, formData);

        // Close the drawer
        setOpen(false);
      },
      onError: (error: any) => {
        console.log(
          'Edit product - Submitting company product update:',
          formData
        );
        // Handle error (e.g., show error message)
        console.error('Edit product - Error updating company product:', error);
        // You might want to set an error state to show to the user
        setErrors((prev) => ({
          ...prev,
          submit: 'Failed to update company product. Please try again.',
        }));
      },
    });

    // Alternatively, if you want to keep the original synchronous behavior:
    // handleChange(event, formData);
    // setOpen(false);
  };

  useEffect(() => {
    if (product) {
      setFormData(ConvertToCompanyProduct(product, companyId || ''));
    }
  }, [product]);

  const buttonWidth = buttonIcon === null ? '100%' : 32;
  const buttonHeight = buttonIcon === null ? 64 : 32;

  return (
    <>
      {buttonIcon === null ? (
        <Button
          variant='contained'
          sx={{
            borderRadius: 2,
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            width: buttonWidth,
            height: buttonHeight,
          }}
          onClick={() => setOpen(true)}
        >
          <Typography
            sx={{
              textTransform: 'uppercase',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontWeight: 'bold',
              fontSize: 14,
              letterSpacing: 2,
              color: 'primary.text',
            }}
          >
            {buttonContext}
          </Typography>
        </Button>
      ) : (
        <Box
          sx={[
            TextStyle.buttonBox,
            {
              width: buttonWidth,
              height: buttonHeight,
            },
          ]}
        >
          <IconButton
            sx={{
              p: 0,
              m: 0,
              borderRadius: 3,
              width: buttonWidth,
              height: buttonHeight,
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
            }}
            onClick={() => setOpen(true)}
          >
            {buttonIcon}
          </IconButton>
        </Box>
      )}

      <Drawer
        anchor='left'
        open={open}
        onClose={() => setOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: width / 2,
            boxSizing: 'border-box',
          },
        }}
      >
        <Box sx={{ p: 3, overflowY: 'auto', maxHeight: '100vh' }}>
          <Typography variant='h5' gutterBottom sx={{ mb: 3 }}>
            {buttonContext || 'Edit Product'}
          </Typography>

          {/* Show mutation loading state */}
          {postCompanyProduct.isLoading && (
            <Alert severity='info' sx={{ mb: 2 }}>
              Updating company product...
            </Alert>
          )}

          {/* Show mutation error */}
          {postCompanyProduct.isError && (
            <Alert severity='error' sx={{ mb: 2 }}>
              Failed to update company product. Please try again.
            </Alert>
          )}

          {Object.keys(errors).length > 0 && (
            <Alert severity='error' sx={{ mb: 2 }}>
              Please fix the errors before submitting
            </Alert>
          )}

          {/* EDITABLE FIELDS */}
          <Typography variant='h6' sx={{ mb: 2, color: 'primary.main' }}>
            Editable Fields
          </Typography>

          {/* ServiceTag - EDIT */}
          <TextField
            fullWidth
            label='Service Tag'
            value={formData.serviceTag || ''}
            onChange={(e) => handleFieldChange('serviceTag', e.target.value)}
            margin='normal'
            variant='outlined'
            required
            disabled={postCompanyProduct.isLoading}
          />

          {/* SerialNumber - EDIT with requirement */}
          <TextField
            fullWidth
            label='Serial Number'
            value={formData.serialNumber || ''}
            onChange={(e) => handleFieldChange('serialNumber', e.target.value)}
            margin='normal'
            variant='outlined'
            error={!!errors.serialNumber}
            helperText={errors.serialNumber}
            disabled={postCompanyProduct.isLoading}
          />

          {/* Lifetime - EDIT */}
          <TextField
            fullWidth
            label='Lifetime'
            type='number'
            value={formData.lifetime || ''}
            onChange={(e) =>
              handleFieldChange('lifetime', parseInt(e.target.value) || 0)
            }
            margin='normal'
            variant='outlined'
            disabled={postCompanyProduct.isLoading}
          />

          <Divider sx={{ my: 3 }} />

          {/* VIEWABLE FIELDS (Read-only) */}
          <Typography variant='h6' sx={{ mb: 2, color: 'text.secondary' }}>
            View Only Fields
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            {/* ProductId - VIEW */}
            <TextField
              fullWidth
              label='Product ID'
              value={formData.productId || ''}
              margin='normal'
              variant='outlined'
              InputProps={{ readOnly: true }}
            />

            {/* OwnerId - VIEW */}
            <TextField
              fullWidth
              label='Owner ID'
              value={formData.ownerId || 'Not assigned'}
              margin='normal'
              variant='outlined'
              InputProps={{ readOnly: true }}
            />

            {/* ResourceId - VIEW */}
            <TextField
              fullWidth
              label='Resource ID'
              value={formData.resourceId || 'Not assigned'}
              margin='normal'
              variant='outlined'
              InputProps={{ readOnly: true }}
            />

            {/* AgreementId - VIEW */}
            {/*   <TextField
              fullWidth
              label='Agreement ID'
              value={formData.agreementId || 'No agreement'}
              margin='normal'
              variant='outlined'
              InputProps={{ readOnly: true }}
            /> */}

            {/* RetailPrice - VIEW */}
            <TextField
              fullWidth
              label='Retail Price'
              value={
                formData.retailPrice ? `$${formData.retailPrice}` : 'Not set'
              }
              margin='normal'
              variant='outlined'
              InputProps={{ readOnly: true }}
            />

            {/* GuaranteeEndDate - VIEW */}
            {/*   <TextField
              fullWidth
              label='Guarantee End Date'
              value={
                formData.guaranteeEndDate
                  ? new Date(formData.guaranteeEndDate).toLocaleDateString()
                  : 'No guarantee'
              }
              margin='normal'
              variant='outlined'
              InputProps={{ readOnly: true }}
            /> */}

            {/* VendorId - VIEW */}
            <TextField
              fullWidth
              label='Vendor ID'
              value={formData.vendorId || 'No vendor'}
              margin='normal'
              variant='outlined'
              InputProps={{ readOnly: true }}
            />

            {/* VendorProductUrl - VIEW */}
            <TextField
              fullWidth
              label='Vendor Product URL'
              value={formData.vendorProductUrl || 'No URL'}
              margin='normal'
              variant='outlined'
              InputProps={{ readOnly: true }}
            />
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}
          >
            <Button
              variant='outlined'
              onClick={() => setOpen(false)}
              disabled={postCompanyProduct.isLoading}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              onClick={handleSubmit}
              disabled={
                Object.keys(errors).length > 0 || postCompanyProduct.isLoading
              }
            >
              {postCompanyProduct.isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};
